/**
 * Web Vitals Utility for Google PageSpeed Insights Metrics
 * 
 * This utility captures Core Web Vitals metrics that Google uses to evaluate
 * page performance in PageSpeed Insights and Search ranking.
 * 
 * Metrics captured:
 * - LCP (Largest Contentful Paint): Loading performance
 * - INP (Interaction to Next Paint): Interactivity (replaced FID)
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FCP (First Contentful Paint): Initial render
 * - TTFB (Time to First Byte): Server response time
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

export interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

// Thresholds based on Google's Core Web Vitals guidelines
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
};

/**
 * Convert web-vitals Metric to our WebVitalsMetric format
 */
function formatMetric(metric: Metric): WebVitalsMetric {
  return {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
    navigationType: metric.navigationType || 'unknown',
  };
}

/**
 * Default handler that logs metrics to console in development
 * and can be extended to send to analytics services
 */
function defaultHandler(metric: WebVitalsMetric): void {
  // Log to console in development for debugging
  if (process.env.NODE_ENV === 'development') {
    const color = metric.rating === 'good' 
      ? '#0CCE6B' 
      : metric.rating === 'needs-improvement' 
        ? '#FFA400' 
        : '#FF4E42';
    
    console.log(
      `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(metric.name === 'CLS' ? 3 : 0)}${metric.name === 'CLS' ? '' : 'ms'} (${metric.rating})`,
      `color: ${color}; font-weight: bold;`
    );
  }
}

/**
 * Send metrics to Google Analytics 4 (if available)
 */
function sendToGoogleAnalytics(metric: WebVitalsMetric): void {
  // Check if gtag is available (Google Analytics 4)
  if (typeof window !== 'undefined' && 'gtag' in window) {
    const gtag = (window as unknown as { gtag: (...args: unknown[]) => void }).gtag;
    gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.delta * 1000 : metric.delta),
      non_interaction: true,
      metric_rating: metric.rating,
      metric_value: metric.value,
      metric_delta: metric.delta,
    });
  }
}

/**
 * Send metrics to a custom analytics endpoint
 */
async function sendToAnalyticsEndpoint(
  metric: WebVitalsMetric,
  endpoint?: string
): Promise<void> {
  if (!endpoint) return;

  const body = JSON.stringify({
    ...metric,
    url: window.location.href,
    timestamp: Date.now(),
    userAgent: navigator.userAgent,
  });

  // Use `navigator.sendBeacon` if available, falling back to `fetch`
  if (navigator.sendBeacon) {
    navigator.sendBeacon(endpoint, body);
  } else {
    try {
      await fetch(endpoint, {
        body,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      });
    } catch (error) {
      console.warn('[Web Vitals] Failed to send metrics:', error);
    }
  }
}

export interface ReportWebVitalsOptions {
  /** Custom handler for processing metrics */
  onMetric?: (metric: WebVitalsMetric) => void;
  /** Whether to send to Google Analytics 4 */
  sendToGA4?: boolean;
  /** Custom analytics endpoint URL */
  analyticsEndpoint?: string;
  /** Whether to report all changes or just final values */
  reportAllChanges?: boolean;
}

/**
 * Initialize Web Vitals reporting
 * 
 * @example
 * // Basic usage - logs to console in development
 * reportWebVitals();
 * 
 * @example
 * // With Google Analytics 4
 * reportWebVitals({ sendToGA4: true });
 * 
 * @example
 * // With custom handler
 * reportWebVitals({
 *   onMetric: (metric) => {
 *     console.log(`${metric.name}: ${metric.value}`);
 *   }
 * });
 * 
 * @example
 * // With custom analytics endpoint
 * reportWebVitals({
 *   analyticsEndpoint: '/api/analytics/vitals'
 * });
 */
export function reportWebVitals(options: ReportWebVitalsOptions = {}): void {
  const {
    onMetric,
    sendToGA4 = false,
    analyticsEndpoint,
    reportAllChanges = false,
  } = options;

  const handleMetric = (metric: Metric) => {
    const formattedMetric = formatMetric(metric);
    
    // Always use default handler for logging
    defaultHandler(formattedMetric);
    
    // Call custom handler if provided
    if (onMetric) {
      onMetric(formattedMetric);
    }
    
    // Send to Google Analytics 4 if enabled
    if (sendToGA4) {
      sendToGoogleAnalytics(formattedMetric);
    }
    
    // Send to custom endpoint if provided
    if (analyticsEndpoint) {
      sendToAnalyticsEndpoint(formattedMetric, analyticsEndpoint);
    }
  };

  const vitalsOptions = { reportAllChanges };

  // Register handlers for all Core Web Vitals
  onCLS(handleMetric, vitalsOptions);
  onFCP(handleMetric, vitalsOptions);
  onINP(handleMetric, vitalsOptions);
  onLCP(handleMetric, vitalsOptions);
  onTTFB(handleMetric, vitalsOptions);
}

/**
 * Get metric rating based on thresholds
 */
export function getMetricRating(
  name: keyof typeof THRESHOLDS,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Get threshold values for a specific metric
 */
export function getMetricThresholds(name: keyof typeof THRESHOLDS) {
  return THRESHOLDS[name];
}

/**
 * Check if the current page has good Core Web Vitals
 * Returns a promise that resolves when all metrics are collected
 */
export function checkCoreWebVitals(): Promise<{
  LCP: WebVitalsMetric | null;
  INP: WebVitalsMetric | null;
  CLS: WebVitalsMetric | null;
  FCP: WebVitalsMetric | null;
  TTFB: WebVitalsMetric | null;
  allGood: boolean;
}> {
  const metrics: Record<string, WebVitalsMetric | null> = {
    LCP: null,
    INP: null,
    CLS: null,
    FCP: null,
    TTFB: null,
  };

  return new Promise((resolve) => {
    // Set a timeout to resolve even if some metrics don't fire
    const timeout = setTimeout(() => {
      resolve({
        ...metrics,
        allGood: Object.values(metrics).every(
          (m) => m === null || m.rating === 'good'
        ),
      } as ReturnType<typeof checkCoreWebVitals> extends Promise<infer T> ? T : never);
    }, 30000); // 30 second timeout

    let metricsCollected = 0;
    const totalMetrics = 5;

    const checkComplete = () => {
      metricsCollected++;
      if (metricsCollected === totalMetrics) {
        clearTimeout(timeout);
        resolve({
          ...metrics,
          allGood: Object.values(metrics).every(
            (m) => m === null || m.rating === 'good'
          ),
        } as ReturnType<typeof checkCoreWebVitals> extends Promise<infer T> ? T : never);
      }
    };

    onCLS((metric) => { metrics.CLS = formatMetric(metric); checkComplete(); });
    onFCP((metric) => { metrics.FCP = formatMetric(metric); checkComplete(); });
    onINP((metric) => { metrics.INP = formatMetric(metric); checkComplete(); });
    onLCP((metric) => { metrics.LCP = formatMetric(metric); checkComplete(); });
    onTTFB((metric) => { metrics.TTFB = formatMetric(metric); checkComplete(); });
  });
}

export default reportWebVitals;
